import {closestCenter, DragEndEvent, DragStartEvent} from '@dnd-kit/core';
import {verticalListSortingStrategy} from '@dnd-kit/sortable';
import {
  Action,
  Form,
  FormControl,
  FormSubmitHandler,
  openDialog,
  Separator,
} from 'platform/components';
import {VStack} from 'platform/foundation';
import {v4 as uuid} from 'uuid';

import {useState} from 'react';
import {useFieldArray, useWatch, UseFormReturn} from 'react-hook-form';

import {isNotNilOrEmpty} from 'ramda-adjunct';

import {
  buildSortableItemId,
  DnDContext,
  parseDndItemId,
  Sortable,
  SortableWrapper,
} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsSection, SettingsTemplate} from '@dms/settings/shared';

import {
  recordAuditDefinitionFormSchema,
  RecordAuditDefinitionFormValue,
  RecordAuditDefinitionTask,
} from '../types/RecordAuditDefinitionFormValue';
import {TaskOperations} from '../types/TaskOperations';
import {
  auditRowsToFormValue,
  buildAuditRows,
  moveAuditRows,
  reorderSections,
  sectionIdAtRow,
} from '../utils/auditRow';
import {recordAuditDefinitionTaskFactory} from '../utils/recordAuditDefinitionTaskFactory';
import {
  addTaskToSection,
  duplicateTaskAt,
  moveTaskToSection,
  moveTaskWithinSection,
  orderTasksForDisplay,
  removeSectionWithTasks,
} from '../utils/taskListOperations';
import {AddSectionButton} from './AddSectionButton';
import {GeneralSection} from './GeneralSection';
import {SectionHeaderRow} from './SectionHeaderRow';
import {SectionRenameDialog} from './SectionRenameDialog';
import {TaskRow} from './TaskRow';

// The editor is a single flat sortable list (sections + tasks share one context).
// Vertical list strategy live-shifts the rows as they are dragged.
const AUDIT_ROWS_CONTAINER_ID = 'audit-rows';

const DEFAULT_SUCCESS_THRESHOLD = 90;

const EMPTY_FORM_VALUE: RecordAuditDefinitionFormValue = {
  name: '',
  resourceIds: [],
  description: '',
  category: null,
  successThreshold: DEFAULT_SUCCESS_THRESHOLD,
  sections: [],
  tasks: [],
};

export interface RecordAuditDefinitionFormProps {
  onSubmit: FormSubmitHandler<RecordAuditDefinitionFormValue>;
  isEditing?: boolean;
  isLoading?: boolean;
  defaultValues?: RecordAuditDefinitionFormValue;
}

export function RecordAuditDefinitionForm(props: RecordAuditDefinitionFormProps) {
  return (
    <Form<RecordAuditDefinitionFormValue>
      defaultValues={props.defaultValues ?? EMPTY_FORM_VALUE}
      experimentalZodSchema={recordAuditDefinitionFormSchema}
      onSubmit={props.onSubmit}
      shouldWatchForUnsavedChanges
    >
      {(control, formApi, navigateWithoutBlocker) => (
        <DefinitionFormContent
          control={control}
          formApi={formApi}
          isEditing={props.isEditing}
          isLoading={props.isLoading}
          onCancel={() =>
            props.isEditing
              ? navigateWithoutBlocker(settingsRoutes.recordAuditDefinition)
              : navigateWithoutBlocker(-1)
          }
        />
      )}
    </Form>
  );
}

interface DefinitionFormContentProps {
  control: FormControl<RecordAuditDefinitionFormValue>;
  formApi: UseFormReturn<RecordAuditDefinitionFormValue>;
  isEditing?: boolean;
  isLoading?: boolean;
  onCancel: () => void;
}

interface TaskRowMeta {
  positionInSection: number;
  sectionTaskCount: number;
}

function DefinitionFormContent(props: DefinitionFormContentProps) {
  const name = useWatch({control: props.formApi.control, name: 'name'});
  const category = useWatch({control: props.formApi.control, name: 'category'});
  const sections = useWatch({control: props.formApi.control, name: 'sections'}) ?? [];
  const tasks = useWatch({control: props.formApi.control, name: 'tasks'}) ?? [];

  const sectionsArray = useFieldArray({control: props.formApi.control, name: 'sections'});
  const tasksArray = useFieldArray({control: props.formApi.control, name: 'tasks'});

  // Mounted row inputs (useController) keep their old values when the whole array is
  // rewritten via replace(). Bumping the generation remounts every row after a
  // structural mutation so the inputs re-read fresh values.
  const [generation, setGeneration] = useState(0);

  // Id of the section currently being dragged. While a section is dragged every
  // task row dims, so the focus shifts to reordering the section headers.
  const [draggedSectionId, setDraggedSectionId] = useState<string | null>(null);

  const totalWeight = tasks.reduce((sum, task) => sum + task.weight, 0);
  const mandatoryCount = tasks.filter((task) => task.isMandatory).length;

  const setTasks = (next: RecordAuditDefinitionTask[]) => {
    tasksArray.replace(next);
    setGeneration((previous) => previous + 1);
  };

  const operations: TaskOperations = {
    moveUp: (flatIndex) => setTasks(moveTaskWithinSection(tasks, flatIndex, -1)),
    moveDown: (flatIndex) => setTasks(moveTaskWithinSection(tasks, flatIndex, 1)),
    moveToSection: (flatIndex, sectionId) =>
      setTasks(moveTaskToSection(tasks, sections, flatIndex, sectionId)),
    duplicate: (flatIndex) => setTasks(duplicateTaskAt(tasks, flatIndex)),
    remove: (flatIndex) => setTasks(tasks.filter((_, index) => index !== flatIndex)),
  };

  const handleAddTask = (sectionId: string | null) =>
    setTasks(addTaskToSection(tasks, sections, sectionId, recordAuditDefinitionTaskFactory()));

  const handleAddSection = () =>
    openDialog(
      <SectionRenameDialog
        name=""
        confirmLabel={i18n.t('general.actions.create')}
        onRename={(sectionName) => sectionsArray.append({id: uuid(), name: sectionName})}
        data-testid={testIds.settings.recordAuditDefinitionCreate('sectionCreate')}
      />,
      {title: i18n.t('page.settings.generalChecklist.createSection'), size: 'small'}
    );

  const handleRenameSection = (sectionId: string, newName: string) => {
    const index = sections.findIndex((section) => section.id === sectionId);
    if (index >= 0) {
      props.formApi.setValue(`sections.${index}.name`, newName, {shouldDirty: true});
    }
  };

  const handleMoveSection = (sectionId: string, delta: number) => {
    const index = sections.findIndex((section) => section.id === sectionId);
    const target = index + delta;
    if (index < 0 || target < 0 || target >= sections.length) {
      return;
    }
    const nextSections = [...sections];
    [nextSections[index], nextSections[target]] = [nextSections[target], nextSections[index]];
    sectionsArray.replace(nextSections);
    setTasks(orderTasksForDisplay(props.formApi.getValues('tasks'), nextSections));
  };

  const handleDeleteSection = (sectionId: string) => {
    // Confirmation is handled by the section header (openDeleteDialog) before this runs.
    const index = sections.findIndex((section) => section.id === sectionId);
    if (index < 0) {
      return;
    }
    sectionsArray.remove(index);
    setTasks(removeSectionWithTasks(props.formApi.getValues('tasks'), sectionId));
  };

  const rows = buildAuditRows(tasks, sections, generation);

  const handleDragStart = (event: DragStartEvent) => {
    const fromIndex = parseDndItemId(event.active.id).position ?? -1;
    const active = rows[fromIndex];
    setDraggedSectionId(active?.kind === 'header' ? active.sectionId : null);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setDraggedSectionId(null);
    if (event.over === null || event.active.id === event.over.id) {
      return;
    }
    const fromIndex = parseDndItemId(event.active.id).position ?? -1;
    const toIndex = parseDndItemId(event.over.id).position ?? -1;
    if (fromIndex < 0 || toIndex < 0) {
      return;
    }
    const active = rows[fromIndex];
    if (active === undefined) {
      return;
    }

    // Section drags reorder whole sections (boundary-snapped via sectionIdAtRow),
    // so a section can never be dropped inside another and absorb its tasks.
    if (active.kind === 'header') {
      if (active.isFixed) {
        return;
      }
      const next = reorderSections(
        sections,
        tasks,
        active.sectionId ?? '',
        sectionIdAtRow(rows, toIndex)
      );
      sectionsArray.replace(next.sections);
      setTasks(next.tasks);
      return;
    }

    const next = auditRowsToFormValue(moveAuditRows(rows, fromIndex, toIndex));
    sectionsArray.replace(next.sections);
    setTasks(next.tasks);
  };

  // Per-task metadata (position within its section) for the move up/down menu items.
  const taskMeta = new Map<number, TaskRowMeta>();
  const groups = new Map<string | null, number[]>();
  tasks.forEach((task, flatIndex) => {
    const group = groups.get(task.sectionId) ?? [];
    group.push(flatIndex);
    groups.set(task.sectionId, group);
  });
  groups.forEach((flatIndexes) => {
    flatIndexes.forEach((flatIndex, position) => {
      taskMeta.set(flatIndex, {
        positionInSection: position,
        sectionTaskCount: flatIndexes.length,
      });
    });
  });

  const sectionStats = (sectionId: string | null) => {
    const sectionTasks = tasks.filter((task) => task.sectionId === sectionId);
    return {
      taskCount: sectionTasks.length,
      points: sectionTasks.reduce((sum, task) => sum + task.weight, 0),
    };
  };

  const headerParameters = [
    i18n.t('page.settings.generalChecklist.headerLabel'),
    isNotNilOrEmpty(category?.trim()) ? category : null,
    i18n.t('page.settings.generalChecklist.taskCount', {count: tasks.length}),
    i18n.t('page.settings.generalChecklist.mandatoryCount', {count: mandatoryCount}),
    i18n.t('page.settings.generalChecklist.totalWeight', {count: totalWeight}),
  ].filter(isNotNilOrEmpty);

  const headerActions: Action[] = [
    {
      type: 'button',
      title: i18n.t('general.actions.cancel'),
      variant: 'secondary',
      onClick: props.onCancel,
      isDisabled: props.isLoading,
    },
    {
      type: 'form-button',
      control: props.control,
      buttonType: 'submit',
      title: i18n.t('general.actions.save'),
      isLoading: props.isLoading,
    },
  ];

  return (
    <SettingsTemplate
      header={{
        // Optional chaining: useWatch returns undefined until the field registers
        // (the create page mounts without defaultValues), mirroring `category` above.
        title: isNotNilOrEmpty(name?.trim())
          ? name
          : i18n.t('page.settings.generalChecklist.newDefinition'),
        subtitle: headerParameters.join(' · '),
        breadcrumbs: [
          {
            label: i18n.t('page.generalSettings.labels.title'),
            isCurrentPage: true,
          },
          {
            label: i18n.t('page.settings.recordAuditDefinition.title'),
            href: settingsRoutes.recordAuditDefinition,
          },
        ],
        actions: headerActions,
      }}
      isCreating={!props.isEditing}
      data-testid={
        props.isEditing
          ? testIds.settings.recordAuditDefinitionDetail()
          : testIds.settings.recordAuditDefinitionCreate('newTemplate')
      }
    >
      <SettingsSection>
        <VStack spacing={4}>
          <GeneralSection
            control={props.control}
            formApi={props.formApi}
            totalWeight={totalWeight}
            data-testid={testIds.settings.recordAuditDefinitionCreate('general')}
          />
          <Separator spacing={0} />
          <DnDContext
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setDraggedSectionId(null)}
          >
            <SortableWrapper
              containerId={AUDIT_ROWS_CONTAINER_ID}
              items={rows.map((row) => ({id: row.id, url: row.id}))}
              strategy={verticalListSortingStrategy}
            >
              <VStack spacing={1}>
                {rows.map((row, rowIndex) => (
                  <Sortable
                    key={row.id}
                    id={buildSortableItemId(row.id, AUDIT_ROWS_CONTAINER_ID, row.id, rowIndex)}
                    withHandle
                    disabledDrag={row.kind === 'header' && row.isFixed}
                  >
                    {({isDragging, handleProps}) =>
                      row.kind === 'header' ? (
                        <SectionHeaderRow
                          name={row.name}
                          taskCount={sectionStats(row.sectionId).taskCount}
                          points={sectionStats(row.sectionId).points}
                          isFixed={row.isFixed}
                          isFirst={sections.findIndex((s) => s.id === row.sectionId) === 0}
                          isLast={
                            sections.findIndex((s) => s.id === row.sectionId) ===
                            sections.length - 1
                          }
                          subtitle={
                            row.isFixed
                              ? i18n.t('page.settings.generalChecklist.unsectionedHint')
                              : undefined
                          }
                          onRename={(newName) => handleRenameSection(row.sectionId ?? '', newName)}
                          onDelete={() => handleDeleteSection(row.sectionId ?? '')}
                          onMoveUp={() => handleMoveSection(row.sectionId ?? '', -1)}
                          onMoveDown={() => handleMoveSection(row.sectionId ?? '', 1)}
                          onAddTask={() => handleAddTask(row.sectionId)}
                          handleProps={handleProps}
                          data-testid={testIds.settings.recordAuditDefinitionCreate(
                            `header[${row.sectionId ?? 'none'}]`
                          )}
                        />
                      ) : (
                        <TaskRow
                          formApi={props.formApi}
                          flatIndex={row.flatIndex}
                          positionInSection={taskMeta.get(row.flatIndex)?.positionInSection ?? 0}
                          sectionTaskCount={taskMeta.get(row.flatIndex)?.sectionTaskCount ?? 1}
                          sectionId={row.task.sectionId}
                          sections={sections}
                          operations={operations}
                          isDragging={isDragging}
                          isDimmed={draggedSectionId !== null}
                          handleProps={handleProps}
                          data-testid={testIds.settings.recordAuditDefinitionCreate(
                            `task[${row.flatIndex}]`
                          )}
                        />
                      )
                    }
                  </Sortable>
                ))}
              </VStack>
            </SortableWrapper>
          </DnDContext>
          <AddSectionButton onClick={handleAddSection} />
        </VStack>
      </SettingsSection>
    </SettingsTemplate>
  );
}
